import { ViewContainerRef } from "@angular/core";
import { Direction } from "@angular/cdk/bidi";
import { ScrollStrategy } from "@angular/cdk/overlay";

export type AutoFocusTarget = "dialog" | "first-tabbable" | "first-heading";

export const VdsDialogAppearance = {
    default: "default",
    dark: "dark",
    gray: "gray"
} as const;

export type VdsDialogAppearanceType = (typeof VdsDialogAppearance)[keyof typeof VdsDialogAppearance];

/** Valid ARIA roles for our dialog element */
export type DialogRole = "dialog" | "alertdialog";

/** Possible overrides for a dialog's position. */
export interface DialogPosition {
    /** Override for the dialog's top position. */
    top?: string;

    /** Override for the dialog's bottom position. */
    bottom?: string;

    /** Override for the dialog's left position. */
    left?: string;

    /** Override for the dialog's right position. */
    right?: string;
}

export class VdsDialogConfig<D = any> {
    /**
     * Where the attached component should live in Angular's *logical* component tree.
     * This affects what is available for injection and the change detection order for
     * the component instantiated inside the dialog. This does not affect where the dialog content will actually be rendered.
     */
    viewContainerRef?: ViewContainerRef;

    /**
     * The appearance (color) of the dialog container.
     */
    appearance?: VdsDialogAppearanceType = "default";

    /**
     * ID for the dialog. If omitted, a unique one will be generated.
     */
    id?: string;

    /**
     * The ARIA role of the dialog element ( for proper usability )
     */
    role?: DialogRole = "dialog";

    /**
     * Custom class for the overlay pane
     */
    panelClass?: string | string[] = "";

    /**
     * Custom class for the backdrop
     */
    hasBackdrop?: boolean = true;

    /**
     * Custom class for the backdrop
     */
    backdropClass?: string | string[] = "vds-dialog-backdrop";

    /**
     * Wether the user can use escape or clicking outside to close modal
     */
    disableClose?: boolean = false;

    /**
     * Width of the dialog
     */
    width?: string = "";

    /**
     * Height of the dialog
     */
    height?: string = "";

    /**
     * Min-width of the dialog. If a number is provided, pixel units are assumed
     */
    minWidth?: number | string;

    /**
     * Min-height of the dialog. If a number is provided, pixel units are assumed
     */
    minHeight?: number | string;

    /**
     * Max-width of the dialog. If a number is provided, pixel units are assumed. Defaults to 80vw
     */
    maxWidth?: number | string;

    /**
     * Max-height of the dialog. If a number is provided, pixel units are assumed
     */
    maxHeight?: number | string;

    /** Position overrides */
    position?: DialogPosition;

    /** Data being injected into the child component */
    data?: D | null = null;

    /** Layout direction for the dialog's content */
    direction?: Direction;

    /** ID of the element that describes the dialog */
    ariaDescribedBy?: string | null = null;

    /** ID of the element that labels the dialog */
    ariaLabelledBy?: string | null = null;

    /** Aria label to assign to the dialog element */
    ariaLabel?: string | null = null;

    /** Whether this is a modal dialog. Used to set the `aria-modal` attribute. */
    ariaModal?: boolean = true;

    /** Whether the dialog should focus the first focusable element on open */
    autoFocus?: AutoFocusTarget | string | boolean = "first-tabbable";

    /**
     * Whether the dialog should restore focus to the
     * previously-focused element, after it's closed.
     */
    restoreFocus?: boolean = true;

    /** Whether to wait for the opening animation to finish before trapping focus. */
    delayFocusTrap?: boolean = true;

    /** Scroll strategy to be used for the dialog */
    scrollStrategy?: ScrollStrategy;

    /** Whether the dialog should close when the user goes backwards/forwards in history */
    closeOnNavigation?: boolean = true;
}
